/**
 * ConvoCX client telemetry (CON-835).
 *
 * Forwards browser errors (uncaught exceptions + unhandled promise rejections)
 * to the backend `/api/errors/client` endpoint so client-side failures are
 * visible in the ops stack (pino logs + Prometheus error metrics). PII is
 * sanitized before sending. Backed by `sendBeacon` so reports survive
 * page unload.
 */
const ENDPOINT = '/api/errors/client';

const MAX_STACK_LENGTH = 2000;
const MAX_URL_LENGTH = 500;

function sanitizeMessage(message: string): string {
  return message
    .replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[email]')
    .slice(0, MAX_STACK_LENGTH);
}

function sanitizeUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.search = '';
    parsed.hash = '';
    parsed.username = '';
    parsed.password = '';
    return parsed.toString().slice(0, MAX_URL_LENGTH);
  } catch {
    return url.split('?')[0].split('#')[0].slice(0, MAX_URL_LENGTH);
  }
}

function sendError(payload: Record<string, unknown>): void {
  const body = JSON.stringify(payload);
  const blob = new Blob([body], { type: 'application/json' });

  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    if (navigator.sendBeacon(ENDPOINT, blob)) return;
  }
  fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {});
}

/** Install window-level error + rejection handlers. Idempotent. */
export function initErrorForwarder(): () => void {
  const origin = window.location.origin;

  const onError = (event: ErrorEvent) => {
    // Skip element-level errors (resource load failures) — forward only
    // script/window errors. jsdom's event target is a Window proxy that is
    // not `=== window`, so detect DOM elements explicitly.
    if (event.target && event.target instanceof Element) return;

    sendError({
      message: sanitizeMessage(event.message || 'Unknown error'),
      stack: event.error?.stack?.slice(0, MAX_STACK_LENGTH) ?? event.message ?? 'Unknown error',
      type: 'error',
      url: sanitizeUrl(event.filename || window.location.href),
      line: event.lineno ?? undefined,
      col: event.colno ?? undefined,
      timestamp: new Date().toISOString(),
      origin,
    });
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const message =
      reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : 'Unhandled promise rejection';

    sendError({
      message: sanitizeMessage(message),
      stack: reason instanceof Error ? reason.stack?.slice(0, MAX_STACK_LENGTH) : undefined,
      type: 'unhandledrejection',
      url: sanitizeUrl(window.location.href),
      timestamp: new Date().toISOString(),
      origin,
    });
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
  };
}